import { Modal, Input, Form, notification, Select } from 'antd';
import { Geocoder } from '@mapbox/search-js-react';
import { Typography } from 'antd';
import { useState, useEffect } from 'react';
import { useUpdateJobPropertyGeolocationMutation } from '../routingQueries';
import { AddJobsRecord } from '../routingApi';
import Map, { Marker, NavigationControl } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

const { Text } = Typography;

type AddressFormData = {
  nickname: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  country: string;
  latitude: number | null;
  longitude: number | null;
}

export type Props = {
  isOpen: boolean;
  onClose: () => void;
  onAddressSelect?: (address: any) => void;
  job: AddJobsRecord | null
  proximity?: string | undefined
  address?: string | null
}

const geocodeAddress = async (address: string): Promise<{ latitude: number; longitude: number } | null> => {
  const token = (window as any).env.MAPBOX_ACCESS_TOKEN;
  const encoded = encodeURIComponent(address);
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?access_token=${token}&limit=1`;

  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.features && data.features.length > 0) {
      const [longitude, latitude] = data.features[0].center;
      return { latitude, longitude };
    }
    return null;
  } catch {
    return null;
  }
};

export const JobAddressModal = ({isOpen, onClose, job, onAddressSelect, proximity = undefined}: Props) => {
  const [formData, setFormData] = useState<AddressFormData>({
    nickname: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    county: '',
    country: '',
    latitude: null,
    longitude: null
  });
  const [isSaving, setIsSaving] = useState(false);
  const { mutateAsync: updateJobPropertyGeolocation } = useUpdateJobPropertyGeolocationMutation();

  useEffect(() => {
    if (isOpen && job) {
      setFormData({
        nickname: job.nickname || '',
        address1: job.address1 || '',
        address2: job.address2 || '',
        city: job.city || '',
        state: job.state || '',
        zip: job.zip || '',
        county: job.county || '',
        country: job.countryCode || '',
        latitude: job.latitude || null,
        longitude: job.longitude || null
      });
    }
  }, [isOpen, job]);

  const handleAddressRetrieve = (res: any) => {
    const context = res.properties?.context || {};
    const addressValue = context.address?.name || res.properties?.address_line1 || '';
    const city = context.place?.name || res.properties?.context_city || '';
    const state = context.region?.region_code || res.properties?.context_region || '';
    const zip = context.postcode?.name || res.properties?.context_postcode || '';
    const county = context.district?.name || '';
    const country = context.country?.country_code || '';
    const latitude = res.geometry?.coordinates?.[1] || null;
    const longitude = res.geometry?.coordinates?.[0] || null;

    setFormData(prev => ({
      nickname: prev.nickname,
      address1: addressValue,
      address2: prev.address2,
      city,
      state,
      zip,
      county,
      country,
      latitude,
      longitude
    }));
  };

  const isFormValid = () => {
    return (
      formData.address1.trim() !== '' &&
      formData.city.trim() !== '' &&
      formData.state.trim() !== '' &&
      formData.zip.trim() !== '' &&
      formData.country.trim() !== ''
    );
  };

  const handleOk = async () => {
    if (!isFormValid()) {
      notification.error({
        message: 'Validation Error',
        description: 'Please fill in all required fields.'
      });
      return;
    }

    if (!job?.propertyId) {
      console.error('[JobAddressModal] Cannot save: job.propertyId is missing', { job });
      onClose();
      return;
    }

    setIsSaving(true);

    let latitude = formData.latitude;
    let longitude = formData.longitude;

    if (latitude === null || longitude === null) {
      const fullAddress = `${formData.address1}, ${formData.city}, ${formData.state} ${formData.zip}, ${formData.country}`;
      const coords = await geocodeAddress(fullAddress);
      if (!coords) {
        notification.error({
          message: 'Geocoding Failed',
          description: 'Unable to find coordinates for this address. Please verify the address and try again.'
        });
        setIsSaving(false);
        return;
      }
      latitude = coords.latitude;
      longitude = coords.longitude;
    }

    onAddressSelect?.({ formData, latitude, longitude });

    try {
      await updateJobPropertyGeolocation({
        propertyId: job.propertyId,
        nickname: formData.nickname,
        address1: formData.address1,
        address2: formData.address2 || null,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        county: formData.county,
        countryCode: formData.country,
        latitude,
        longitude
      });
    } catch (error) {
      console.error('[JobAddressModal] Failed to update geolocation:', error);
      notification.error({
        message: 'Save Failed',
        description: 'Failed to save the address. Please try again.'
      });
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const updateField = (field: keyof AddressFormData, value: string) => {
    const addressFields = ['address1', 'city', 'state', 'zip', 'country'];
    const shouldClearCoords = addressFields.includes(field);
    setFormData(prev => ({
      ...prev,
      [field]: value,
      latitude: shouldClearCoords ? null : prev.latitude,
      longitude: shouldClearCoords ? null : prev.longitude
    }));
  };

  const defaultCenter = { latitude: 39.8283, longitude: -98.5795 };
  const mapCenter = formData.latitude && formData.longitude
    ? { latitude: formData.latitude, longitude: formData.longitude }
    : defaultCenter;

  const [viewState, setViewState] = useState({
    longitude: mapCenter.longitude,
    latitude: mapCenter.latitude,
    zoom: formData.latitude && formData.longitude ? 14 : 3
  });

  useEffect(() => {
    if (formData.latitude && formData.longitude) {
      setViewState({
        longitude: formData.longitude,
        latitude: formData.latitude,
        zoom: 14
      });
    }
  }, [formData.latitude, formData.longitude]);

  return (
    <Modal
      open={isOpen}
      onCancel={handleCancel}
      onOk={handleOk}
      okText="Save"
      cancelText="Cancel"
      width={1000}
      okButtonProps={{
        disabled: !job || !isFormValid(),
        loading: isSaving
      }}
    >
      <div>
        <div>
          <Text strong style={{ fontSize: '18px' }}>{job?.jobName}</Text>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <Text>{job?.customerDisplayName}</Text>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '16px' }}>
        <div style={{ flex: 1 }}>
          <Form layout="vertical">
            <Form.Item label="Property Nickname" style={{ marginBottom: '12px' }}>
              <Input
                value={formData.nickname}
                onChange={(e) => updateField('nickname', e.target.value)}
                placeholder="Property Nickname (optional)"
              />
            </Form.Item>

            <Form.Item label="Address" required style={{ marginBottom: '12px' }}>
              <Geocoder
                key={`geocoder-job-address`}
                accessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
                placeholder="Search for an address"
                value={formData.address1 || undefined}
                onRetrieve={handleAddressRetrieve}
                theme={{variables: {
                  borderRadius: '6px',
                  colorPrimary: '#1677ff',
                  colorBackground: '#ffffff',
                  colorText: 'rgba(0, 0, 0, 0.88)',
                  colorSecondary: 'rgba(0, 0, 0, 0.45)',
                }}}
                options={{
                  proximity: proximity
                }}
              />
            </Form.Item>

            <Form.Item label="Address 2" style={{ marginBottom: '12px' }}>
              <Input
                value={formData.address2}
                onChange={(e) => updateField('address2', e.target.value)}
                placeholder="Apt, Suite, Unit, etc. (optional)"
              />
            </Form.Item>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Form.Item label="City" required style={{ flex: 1, marginBottom: '12px' }}>
                <Input
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  placeholder="City"
                />
              </Form.Item>
              <Form.Item label="State" required style={{ width: '80px', marginBottom: '12px' }}>
                <Input
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value)}
                  placeholder="State"
                />
              </Form.Item>
              <Form.Item label="Zip" required style={{ width: '100px', marginBottom: '12px' }}>
                <Input
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                  placeholder="Zip"
                />
              </Form.Item>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Form.Item label="County" style={{ flex: 1, marginBottom: '12px' }}>
                <Input
                  value={formData.county}
                  onChange={(e) => updateField('county', e.target.value)}
                  placeholder="County (optional)"
                />
              </Form.Item>
              <Form.Item label="Country" required style={{ width: '120px', marginBottom: '12px' }}>
                <Select
                  value={formData.country || undefined}
                  onChange={(value) => updateField('country', value)}
                  placeholder="Select Country"
                  options={[
                    { value: 'US', label: 'US' },
                    { value: 'CA', label: 'CA' }
                  ]}
                />
              </Form.Item>
            </div>

          </Form>
        </div>

        <div style={{ width: '500px', height: '400px', borderRadius: '8px', overflow: 'hidden' }}>
          <Map
            mapboxAccessToken={(window as any).env.MAPBOX_ACCESS_TOKEN}
            {...viewState}
            onMove={evt => setViewState(evt.viewState)}
            onClick={(evt) => {
              setFormData(prev => ({
                ...prev,
                latitude: evt.lngLat.lat,
                longitude: evt.lngLat.lng
              }));
            }}
            style={{ width: '100%', height: '100%', cursor: 'crosshair' }}
            mapStyle="mapbox://styles/mapbox/satellite-v9"
          >
            <NavigationControl position="top-right" showCompass={false} />
            {formData.latitude && formData.longitude && (
              <Marker
                latitude={formData.latitude}
                longitude={formData.longitude}
                anchor="bottom"
                draggable
                onDragEnd={(evt) => {
                  setFormData(prev => ({
                    ...prev,
                    latitude: evt.lngLat.lat,
                    longitude: evt.lngLat.lng
                  }));
                }}
              >
                <svg
                  width="32"
                  height="42"
                  viewBox="0 0 32 42"
                  fill="none"
                  style={{ cursor: 'grab' }}
                >
                  <path
                    d="M16 0C7.163 0 0 7.163 0 16c0 10 16 26 16 26s16-16 16-26c0-8.837-7.163-16-16-16z"
                    fill="#e53935"
                    stroke="#b71c1c"
                    strokeWidth="1"
                  />
                  <circle cx="16" cy="16" r="6" fill="#b71c1c" />
                </svg>
              </Marker>
            )}
          </Map>
        </div>
      </div>
    </Modal>
  );
};
